import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  approveOAuth,
  getJwt,
  getMe,
  setSsoSession,
} from "../lib/api";
import { SiteFooter } from "../components/SiteFooter";

// Landing page for the in-SPA OAuth consent flow. /oauth/authorize deep-links
// here (with ?code=&client=) when there's no SSO cookie. If the user is signed
// into the SPA we offer a one-click Approve (no email); otherwise we fall back
// to the magic-link email, carrying the same oauth code so the link completes
// the connection.
export function Connect() {
  const [params] = useSearchParams();
  const code = params.get("code") ?? "";
  const clientName = params.get("client") ?? "An MCP client";

  const loggedIn = !!getJwt();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: loggedIn,
    retry: false,
  });

  // Bridge the localStorage session into the SSO cookie so the *next*
  // connection from this browser is a silent auto-approve.
  useEffect(() => {
    if (loggedIn) void setSsoSession();
  }, [loggedIn]);

  const [state, setState] = useState<"idle" | "working" | "error" | "sent">(
    "idle"
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");

  const onApprove = async () => {
    if (!code) return;
    setState("working");
    setErrorMsg("");
    try {
      const { redirect } = await approveOAuth(code);
      window.location.assign(redirect);
    } catch (err) {
      setState("error");
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  const onEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !code) return;
    setState("working");
    setErrorMsg("");
    try {
      await fetch("/oauth/email", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email: email.trim(), oauth_code: code }),
      });
      setState("sent");
    } catch (err) {
      setState("error");
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 grid place-items-center px-6">
        <div className="max-w-sm w-full space-y-4">
          <div className="font-mono text-xs uppercase tracking-wider text-fg-2">
            Connect
          </div>
          <h1 className="text-2xl font-semibold">Authorize access</h1>
          <p className="text-fg-1 text-sm">
            <span className="text-accent font-medium">{clientName}</span> wants
            to access your MCPBuild account via MCP.
          </p>

          {!code && (
            <div className="text-red-400 text-sm">
              This connection link is missing its code. Restart the connection
              from your MCP client.
            </div>
          )}

          {code && state === "sent" && (
            <div className="text-fg-1 text-sm">
              Check your inbox for a sign-in link to{" "}
              <strong>{email}</strong>. Opening it finishes the connection — you
              can close this tab.
            </div>
          )}

          {code && state !== "sent" && loggedIn && (
            <div className="space-y-3">
              {me.data && (
                <div className="text-fg-2 text-xs font-mono">
                  signed in as {me.data.email}
                </div>
              )}
              <button
                onClick={onApprove}
                disabled={state === "working"}
                className="w-full px-4 py-2 rounded-md bg-accent text-white text-sm disabled:opacity-50"
              >
                {state === "working" ? "Connecting…" : `Approve ${" "}connection`}
              </button>
              <p className="text-fg-2 text-xs">
                No email needed — you're already signed in.
              </p>
            </div>
          )}

          {code && state !== "sent" && !loggedIn && (
            <form onSubmit={onEmail} className="space-y-3">
              <p className="text-fg-2 text-xs">
                Sign in to approve. We'll email a one-time link that completes
                the connection.
              </p>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-md bg-bg-1 border border-bg-2 px-3 py-2 text-fg-0"
              />
              <button
                type="submit"
                disabled={state === "working"}
                className="w-full px-4 py-2 rounded-md bg-accent text-white text-sm disabled:opacity-50"
              >
                {state === "working" ? "Sending…" : "Send sign-in link"}
              </button>
            </form>
          )}

          {state === "error" && (
            <div className="text-red-400 text-sm font-mono">{errorMsg}</div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
